import { readSourceDocument, type SourceDocument } from "../../../api/content/sourcesApi";
import { SharedReads } from "../../../shared/loading/sharedReads";
import { formatDocumentVersionRef, type DocumentVersionRef } from "../../../shared/references/contentReferences";
import { documentDisplayName } from "./documentNames";

/** Reads exact document versions and their names; a session's cache, or straight from the server. */
export interface SourceDocumentReader {
  read(reference: DocumentVersionRef, signal: AbortSignal): Promise<SourceDocument>;
  /** The document's name (`documentDisplayName`). */
  name(reference: DocumentVersionRef, signal: AbortSignal): Promise<string>;
  /** Call `listener` whenever the version or its name is read; returns the way to stop. */
  watch(reference: DocumentVersionRef, listener: () => void): () => void;
}

/** Signed out, nothing is kept: each read goes to the server. */
export const READ_THROUGH: SourceDocumentReader = {
  read: readSourceDocument,
  name: (reference, signal) => readSourceDocument(reference, signal).then(documentDisplayName),
  watch: () => () => undefined,
};

/** About a Thread's worth of documents: its subject, and those its results name. */
const TEXTS = 16;
/** Names are small: once a sidebar of Threads has named its subjects, it reads no text again. */
const NAMES = 256;

/**
 * Exact document versions read in one signed-in session (`SourceDocumentProvider`). A
 * version never changes, so one read serves every view of it: a step's result, the review
 * that shows it again, the subject beside the Thread, a name in a heading. At most `TEXTS`
 * texts are kept; names are kept apart, so naming a document reads its text at most once
 * per session. Sharing, bounds, failures and aborts are `SharedReads`'s.
 */
export class SourceDocumentCache implements SourceDocumentReader {
  private readonly texts = new SharedReads<SourceDocument>(TEXTS);
  private readonly names = new SharedReads<string>(NAMES);

  constructor(
    private readonly load: (reference: DocumentVersionRef, signal: AbortSignal) => Promise<SourceDocument> = readSourceDocument,
  ) {}

  read(reference: DocumentVersionRef, signal: AbortSignal): Promise<SourceDocument> {
    return this.texts.read(formatDocumentVersionRef(reference), (shared) => this.load(reference, shared), signal);
  }

  name(reference: DocumentVersionRef, signal: AbortSignal): Promise<string> {
    return this.names.read(formatDocumentVersionRef(reference), (shared) => this.read(reference, shared).then(documentDisplayName), signal);
  }

  watch(reference: DocumentVersionRef, listener: () => void): () => void {
    const key = formatDocumentVersionRef(reference);
    const stops = [this.texts.watch(key, listener), this.names.watch(key, listener)];
    return () => { stops.forEach((stop) => { stop(); }); };
  }

  clear(): void {
    this.texts.clear();
    this.names.clear();
  }
}
